import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";

import { useProduct } from "hooks/useProduct";
import { useProfile } from "hooks/useProfile";

import { MainPageLayout } from "components/MainPageLayout";

import { modalIds } from "constants/modals-constants";
import { useModal } from "hooks/useModal";
import useRequest from "hooks/user/useRequest";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import { useNavigate } from "react-router-dom";
import { TeacherHeader } from "../Header/TeacherHeader";
import { TeacherNavigationMenu } from "../NavigationMenu/TeacherNavigationMenu";

interface TeacherPageLayoutProps {
  children: React.ReactNode;
  withMenu?: boolean;
}

export const TeacherPageLayout: React.FC<TeacherPageLayoutProps> = observer(
  ({ children, withMenu }) => {
    const { fetchProducts } = useProduct();
    const { fetchUserDetails, userDetails, updateUserDetails } = useProfile();
    const { logout } = useRequest();
    const navigate = useNavigate();

    const { toggleModal } = useModal();
    const selectedProductId = useSelectedProductId();

    useEffect(() => {
      fetchProducts();
      fetchUserDetails();
    }, []);

    const onToggleSideMenu = () => {
      toggleModal(modalIds.modalSideMenu);
    };

    const navigateToStudentEnvironment = () => {
      if (selectedProductId) {
        return navigate(
          buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS, {
            productId: selectedProductId,
          }),
        );
      }
    };

    return (
      <MainPageLayout
        header={
          <TeacherHeader
            toggleSideMenu={onToggleSideMenu}
            updateUserDetails={updateUserDetails}
            userDetails={userDetails}
            onLogout={logout}
            navigateToStudentEnvironment={navigateToStudentEnvironment}
          />
        }
        navigationMenu={
          <TeacherNavigationMenu
            className="aside-menu"
            userDetails={userDetails}
            shouldShowSideNav={!!withMenu}
            dataCy="product-navigation-menu"
          />
        }
        shouldShowSideNav={!!withMenu}
        shouldShowHeader={false}
      >
        {children}
      </MainPageLayout>
    );
  },
);
